import { getAuth } from 'firebase/auth';
import {
  getDatabase,
  ref,
  child,
  get,
  update,
  push,
} from 'firebase/database';
import moment from 'moment-timezone';
import {
  all,
  call,
  put,
  select,
  takeEvery,
  takeLatest,
} from 'redux-saga/effects';
import actions from './actions';
import authActions from '../auth/actions';
import { notification } from '../../components';
import { buildCampaignStats, getToday, resolveNpsRole } from '../../helpers/nps';

// Tudo sob o nó `nps`: campanhas e ponteiro da ativa são lidos por todos;
// respostas e dispensas ficam sob o uid de quem escreveu e só o admin lê o conjunto.
const CAMPAIGNS_PATH = 'nps/campaigns';
const ACTIVE_PATH = 'nps/active';
const RESPONSES_PATH = 'nps/responses';
const DISMISSALS_PATH = 'nps/dismissals';

const getAuthFromStore = (state) => state.Auth;
const getProfileFromStore = (state) => state.Profile;
const getCampaignsFromStore = (state) => state.Nps.campaigns;
const getActiveCampaignFromStore = (state) => state.Nps.activeCampaign;

function getNode(path) {
  const db = getDatabase();
  return get(ref(db, path));
}

function isPermissionDenied(error) {
  return String(error?.code || error?.message || '').includes('PERMISSION_DENIED');
}

// --------------- Admin ---------------

function* fetchNpsAdminData() {
  try {
    const [campaignsSnapshot, responsesSnapshot, dismissalsSnapshot] = yield all([
      call(getNode, CAMPAIGNS_PATH),
      call(getNode, RESPONSES_PATH),
      call(getNode, DISMISSALS_PATH),
    ]);
    const campaigns = campaignsSnapshot.val() || {};
    const responses = responsesSnapshot.val() || {};
    const dismissals = dismissalsSnapshot.val() || {};
    const list = Object.keys(campaigns)
      .map((id) => buildCampaignStats(id, campaigns[id], responses[id], dismissals[id]))
      .sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
    yield put({ type: actions.FETCH_NPS_ADMIN_DATA_SUCCESS, payload: list });
  } catch (error) {
    console.warn(error);
    yield put({ type: actions.FETCH_NPS_ADMIN_DATA_ERROR });
    notification('error', 'Não foi possível carregar as campanhas de NPS.');
  }
}

function* createNpsCampaign(action) {
  try {
    const { currentUser } = getAuth();
    const db = getDatabase();
    const campaigns = yield select(getCampaignsFromStore);
    const id = push(child(ref(db), CAMPAIGNS_PATH)).key;
    const updates = {
      [`${CAMPAIGNS_PATH}/${id}`]: {
        ...action.payload,
        status: 'active',
        createdAt: Date.now(),
        createdBy: currentUser.uid,
      },
      [ACTIVE_PATH]: id,
    };
    campaigns
      .filter((campaign) => campaign.status === 'active')
      .forEach((campaign) => {
        updates[`${CAMPAIGNS_PATH}/${campaign.id}/status`] = 'closed';
      });
    yield call(() => update(ref(db), updates));
    yield put({ type: actions.CREATE_NPS_CAMPAIGN_SUCCESS });
    yield put({ type: actions.FETCH_NPS_ADMIN_DATA });
    yield put({ type: actions.FETCH_ACTIVE_NPS });
    notification('success', 'Campanha criada e ativada.');
  } catch (error) {
    console.warn(error);
    yield put({ type: actions.CREATE_NPS_CAMPAIGN_ERROR });
    notification('error', 'Não foi possível criar a campanha. Tente novamente.');
  }
}

function* updateNpsCampaign(action) {
  const { id, ...fields } = action.payload;
  try {
    const db = getDatabase();
    const updates = {};
    Object.keys(fields).forEach((field) => {
      updates[`${CAMPAIGNS_PATH}/${id}/${field}`] = fields[field];
    });
    yield call(() => update(ref(db), updates));
    yield put({ type: actions.UPDATE_NPS_CAMPAIGN_SUCCESS });
    yield put({ type: actions.FETCH_NPS_ADMIN_DATA });
    yield put({ type: actions.FETCH_ACTIVE_NPS });
    notification('success', 'Campanha atualizada.');
  } catch (error) {
    console.warn(error);
    yield put({ type: actions.UPDATE_NPS_CAMPAIGN_ERROR });
    notification('error', 'Não foi possível salvar a campanha. Tente novamente.');
  }
}

// Ativar fecha as outras e aponta o ponteiro; desativar fecha e limpa o ponteiro.
function* setNpsCampaignActive(action) {
  const { id, active } = action.payload;
  try {
    const db = getDatabase();
    const campaigns = yield select(getCampaignsFromStore);
    const updates = {};
    if (active) {
      campaigns
        .filter((campaign) => campaign.status === 'active' && campaign.id !== id)
        .forEach((campaign) => {
          updates[`${CAMPAIGNS_PATH}/${campaign.id}/status`] = 'closed';
        });
      updates[`${CAMPAIGNS_PATH}/${id}/status`] = 'active';
      updates[ACTIVE_PATH] = id;
    } else {
      updates[`${CAMPAIGNS_PATH}/${id}/status`] = 'closed';
      const activeSnapshot = yield call(getNode, ACTIVE_PATH);
      if (activeSnapshot.val() === id) {
        updates[ACTIVE_PATH] = null;
      }
    }
    yield call(() => update(ref(db), updates));
    yield put({ type: actions.SET_NPS_CAMPAIGN_ACTIVE_SUCCESS });
    yield put({ type: actions.FETCH_NPS_ADMIN_DATA });
    yield put({ type: actions.FETCH_ACTIVE_NPS });
    notification('success', active ? 'Campanha ativada.' : 'Campanha desativada.');
  } catch (error) {
    console.warn(error);
    yield put({ type: actions.SET_NPS_CAMPAIGN_ACTIVE_ERROR });
    notification('error', 'Não foi possível alterar a campanha. Tente novamente.');
  }
}

// --------------- Widget ---------------

function* fetchActiveNps() {
  try {
    const auth = yield select(getAuthFromStore);
    const { currentUser } = getAuth();
    if (!auth.professional || !currentUser) {
      return;
    }
    const activeSnapshot = yield call(getNode, ACTIVE_PATH);
    const id = activeSnapshot.val();
    if (!id) {
      yield put({ type: actions.FETCH_ACTIVE_NPS_SUCCESS, payload: { campaign: null } });
      return;
    }
    const campaignSnapshot = yield call(getNode, `${CAMPAIGNS_PATH}/${id}`);
    const campaign = campaignSnapshot.val();
    if (!campaign || campaign.status !== 'active' || campaign.endDate < getToday()) {
      yield put({ type: actions.FETCH_ACTIVE_NPS_SUCCESS, payload: { campaign: null } });
      return;
    }
    const [responseSnapshot, dismissalSnapshot] = yield all([
      call(getNode, `${RESPONSES_PATH}/${id}/${currentUser.uid}`),
      call(getNode, `${DISMISSALS_PATH}/${id}/${currentUser.uid}`),
    ]);
    yield put({
      type: actions.FETCH_ACTIVE_NPS_SUCCESS,
      payload: {
        campaign: { id, ...campaign },
        responded: responseSnapshot.exists(),
        dismissedAt: dismissalSnapshot.val() || null,
      },
    });
  } catch (error) {
    console.warn(error);
    yield put({ type: actions.FETCH_ACTIVE_NPS_ERROR });
  }
}

function resolveUserName(profile, currentUser) {
  if (profile.customUserProfile?.fullName) {
    return profile.customUserProfile.fullName;
  }
  const fullName = `${profile.profile?.firstName || ''} ${profile.profile?.lastName || ''}`.trim();
  return fullName || currentUser.displayName || currentUser.email || null;
}

// "Clínica X · usa há 2 anos": nome da conta principal e tempo desde a criação do usuário.
function buildUserContext(profile, currentUser) {
  const account = `${profile.profile?.firstName || ''} ${profile.profile?.lastName || ''}`.trim();
  const creationTime = currentUser.metadata?.creationTime;
  const since = creationTime ? `usa há ${moment(creationTime).fromNow(true)}` : null;
  return [account || null, since].filter(Boolean).join(' · ') || null;
}

function* submitNpsResponse(action) {
  try {
    const campaign = yield select(getActiveCampaignFromStore);
    if (!campaign) {
      return;
    }
    const { currentUser } = getAuth();
    const auth = yield select(getAuthFromStore);
    const profile = yield select(getProfileFromStore);
    const db = getDatabase();
    const response = {
      score: action.payload.score,
      comment: action.payload.comment?.trim() || null,
      role: resolveNpsRole({ Auth: auth, Profile: profile }),
      name: resolveUserName(profile, currentUser),
      context: buildUserContext(profile, currentUser),
      accountUid: auth.mainUser || currentUser.uid,
      createdAt: Date.now(),
    };
    yield call(() => update(ref(db), {
      [`${RESPONSES_PATH}/${campaign.id}/${currentUser.uid}`]: response,
    }));
    yield put({ type: actions.SUBMIT_NPS_RESPONSE_SUCCESS });
    notification('success', 'Obrigado pela sua resposta.');
  } catch (error) {
    console.warn(error);
    if (isPermissionDenied(error)) {
      yield put({ type: actions.SUBMIT_NPS_RESPONSE_SUCCESS });
      return;
    }
    yield put({ type: actions.SUBMIT_NPS_RESPONSE_ERROR });
    notification('error', 'Não foi possível enviar sua resposta. Tente novamente.');
  }
}

// Falha ao gravar a dispensa não deve manter o cartão na tela: esconde mesmo assim.
function* dismissNps() {
  const now = Date.now();
  try {
    const campaign = yield select(getActiveCampaignFromStore);
    const { currentUser } = getAuth();
    if (campaign && currentUser) {
      const db = getDatabase();
      yield call(() => update(ref(db), {
        [`${DISMISSALS_PATH}/${campaign.id}/${currentUser.uid}`]: now,
      }));
    }
  } catch (error) {
    console.warn(error);
  }
  yield put({ type: actions.DISMISS_NPS_SUCCESS, payload: now });
}

export default function* npsSaga() {
  yield all([
    takeLatest(actions.FETCH_NPS_ADMIN_DATA, fetchNpsAdminData),
    takeEvery(actions.CREATE_NPS_CAMPAIGN, createNpsCampaign),
    takeEvery(actions.UPDATE_NPS_CAMPAIGN, updateNpsCampaign),
    takeEvery(actions.SET_NPS_CAMPAIGN_ACTIVE, setNpsCampaignActive),
    takeLatest([actions.FETCH_ACTIVE_NPS, authActions.LOGIN_SUCCESS], fetchActiveNps),
    takeEvery(actions.SUBMIT_NPS_RESPONSE, submitNpsResponse),
    takeEvery(actions.DISMISS_NPS, dismissNps),
  ]);
}
